import React, { useState, useRef, useCallback, useMemo } from 'react';
import cn from 'clsx';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { useEffectOnce } from 'common/hooks/use-effect-once';
import { useIsomorphicLayoutEffect } from 'common/hooks/use-isomorphic-layout-effect';
import { useBottomOffset, useTopOffset } from 'common/hooks/use-top-offset';

type StyledStickyPanelProps = {
  alwaysOnTop: boolean;
  bottomOffset: number;
  isFixed: boolean;
  topOffset: number;
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const StyledStickyPanel = styled.div<StyledStickyPanelProps>`
  && {
    position: relative;
    top: ${({ topOffset, isFixed }) => (isFixed ? `${topOffset}px` : 'unset')};
    width: 100%;
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    @media (--only-tablet) {
      bottom: ${({ alwaysOnTop, bottomOffset, isFixed }) =>
        isFixed && !alwaysOnTop ? `${bottomOffset}px` : 'unset'};
    }

    @media (--only-mobile) {
      bottom: ${({ alwaysOnTop, bottomOffset, isFixed }) =>
        isFixed && !alwaysOnTop ? `${16 + bottomOffset}px` : 'unset'};
    }
  }
`;

const fixedPanel = css`
  &&& {
    position: fixed;
    z-index: 900;
  }
`;

export const bottomPanel = css`
  &&&& {
    @media (--to-desktop) {
      top: unset;
    }
  }
`;

type StickyPanelProps = {
  alwaysOnTop?: boolean;
  className?: string;
  fixedClassName?: string;
  renderContent: (fixed: boolean) => React.ReactNode;
};

/**
 * Компонент с фиксацией к верху/низу при скролинге страницы
 */
export const StickyPanel: React.FC<StickyPanelProps> = ({
  alwaysOnTop = true,
  className,
  fixedClassName,
  renderContent,
}) => {
  const fixedBorderRef = useRef<HTMLDivElement | null>(null);
  const [isPanelFixed, setIsPanelFixed] = useState<boolean>(false);
  const topOffset = useTopOffset();
  const bottomOffset = useBottomOffset();

  const panelClassNames = useMemo(() => {
    let classNames = cn(className, {
      [fixedPanel]: isPanelFixed,
    });
    if (fixedClassName) {
      classNames = cn(classNames, { [fixedClassName]: isPanelFixed });
    }

    if (!alwaysOnTop) {
      classNames = cn(classNames, { [bottomPanel]: !alwaysOnTop && isPanelFixed });
    }

    return classNames;
  }, [isPanelFixed, className, fixedClassName, alwaysOnTop]);

  /**
   * Обработчик скрола
   */
  const handleScroll = useCallback(() => {
    if (fixedBorderRef) {
      const heightVh = fixedBorderRef.current?.offsetTop || 0;

      setIsPanelFixed(window.scrollY > heightVh);
    }
  }, [fixedBorderRef]);

  /**
   * Определение положение панели при первом рендере
   */
  useEffectOnce(() => {
    handleScroll();
  });

  /**
   * Добавляет событие "scroll" для фиксации панели табов сверху при скроле
   */
  useIsomorphicLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      <div ref={fixedBorderRef} />
      <StyledStickyPanel
        alwaysOnTop={alwaysOnTop}
        bottomOffset={bottomOffset}
        className={panelClassNames}
        isFixed={isPanelFixed}
        topOffset={topOffset}>
        {renderContent(isPanelFixed)}
      </StyledStickyPanel>
    </>
  );
};
